import {FC, useEffect, useRef, useState} from 'react'
import {useThemeMode} from '../../../_metronic/partials'
import {getCSSVariableValue} from '../../../_metronic/assets/ts/_utils'
import {restRequest} from '../../../api-utils/apiQueries'

type Props = {
  chartSize?: number
  chartLine?: number
  chartRotate?: number
}

const TotalUsers: FC<Props> = ({chartSize = 132, chartLine = 14, chartRotate = 145}) => {
  const chartRef: any = useRef<HTMLDivElement | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const {mode} = useThemeMode()
  const [networkData, setNetworkData] = useState<any>(null)

  const fetchData = async () => {
    try {
      const response: any = await restRequest.getDashboardData()
      const data = response.data.data
      setNetworkData(data)
    } catch {
      /* 401: global axios handler redirects; avoid infinite spinner */
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (!isLoading && networkData) {
      refreshChart()
    }
  }, [mode, isLoading, networkData])

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    setTimeout(() => {
      initChart(
        chartRef.current,
        chartSize,
        chartLine,
        chartRotate,
        networkData.drivers,
        networkData.passengers,
        networkData.both
      )
    }, 10)
  }

  return (
    <>
      {isLoading ? (
        <div className='card card-flush h-250px d-flex align-items-center justify-content-center'>
          <div className='spinner-border text-primary'></div>
        </div>
      ) : (
        <div className='card card-flush border-0 shadow-sm'>
          <div className='card-body p-8'>
            <div className='row g-8 align-items-center'>
              <div className='col-xl-4'>
                <div className='d-flex align-items-center gap-6'>
                  <div
                    id='kt_card_widget_17_chart'
                    ref={chartRef}
                    className='flex-shrink-0'
                    style={{minWidth: chartSize + 'px', minHeight: chartSize + 'px'}}
                    data-kt-size={chartSize}
                    data-kt-line={chartLine}
                  ></div>
                  <div>
                    <div className='text-muted fw-semibold mb-1'>Total Shrido Users on Network</div>
                    <div className='fs-3x fw-bold text-gray-900 lh-1'>
                      {networkData ? networkData.users : 0}
                    </div>
                  </div>
                </div>
              </div>

              <div className='col-xl-8'>
                <div className='row g-4'>
                  <div className='col-md-4'>
                    <div className='bg-light-success rounded p-5 h-100'>
                      <div className='d-flex align-items-center mb-2'>
                        <div className='bullet w-8px h-8px rounded-circle bg-success me-3'></div>
                        <span className='text-success fw-semibold'>Drivers</span>
                      </div>
                      <div className='fs-2 fw-bold text-gray-900'>
                        {networkData ? networkData.drivers : 0}
                      </div>
                    </div>
                  </div>
                  <div className='col-md-4'>
                    <div className='bg-light-primary rounded p-5 h-100'>
                      <div className='d-flex align-items-center mb-2'>
                        <div className='bullet w-8px h-8px rounded-circle bg-primary me-3'></div>
                        <span className='text-primary fw-semibold'>Passengers</span>
                      </div>
                      <div className='fs-2 fw-bold text-gray-900'>
                        {networkData ? networkData.passengers : 0}
                      </div>
                    </div>
                  </div>
                  <div className='col-md-4'>
                    <div className='bg-light rounded p-5 h-100'>
                      <div className='d-flex align-items-center mb-2'>
                        <div
                          className='bullet w-8px h-8px rounded-circle me-3'
                          style={{backgroundColor: '#A1A5B7'}}
                        ></div>
                        <span className='text-gray-600 fw-semibold'>Both</span>
                      </div>
                      <div className='fs-2 fw-bold text-gray-900'>
                        {networkData ? networkData.both : 0}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const initChart = function (
  el: HTMLDivElement,
  chartSize: number,
  chartLine: number,
  chartRotate: number,
  drivers: number | undefined,
  passengers: number | undefined,
  both: number | undefined
) {
  if (!el) {
    return
  }
  el.innerHTML = ''

  const driverCount = Number(drivers) || 0
  const passengerCount = Number(passengers) || 0
  const bothCount = Number(both) || 0

  const options = {
    size: chartSize,
    lineWidth: chartLine,
    rotate: chartRotate,
  }

  const canvas = document.createElement('canvas')
  const span = document.createElement('span')

  if (typeof (window as any).G_vmlCanvasManager !== 'undefined') {
    ;(window as any).G_vmlCanvasManager.initElement(canvas)
  }

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  canvas.width = canvas.height = options.size

  // Set background color
  const backgroundColor = getCSSVariableValue('--bs-light') // Replace with your desired background color CSS variable
  canvas.style.backgroundColor = backgroundColor

  el.appendChild(span)
  el.appendChild(canvas)

  ctx.translate(options.size / 2, options.size / 2) // change center
  ctx.rotate((-1 / 2 + options.rotate / 180) * Math.PI) // rotate -90 deg

  const radius = (options.size - options.lineWidth) / 2

  const drawCircle = function (
    color: string,
    lineWidth: number,
    startPercent: number,
    endPercent: number
  ) {
    ctx.beginPath()
    ctx.arc(0, 0, radius, Math.PI * 2 * startPercent, Math.PI * 2 * endPercent, false)
    ctx.strokeStyle = color
    ctx.lineCap = 'round' // butt, round or square
    ctx.lineWidth = lineWidth
    ctx.stroke()
  }

  const total = driverCount + passengerCount + bothCount
  if (total <= 0) {
    ctx.fillStyle = getCSSVariableValue('--bs-gray-500')
    ctx.font = '14px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('No users on network', 0, 5)
    return
  }
  const driversPercent = driverCount / total
  const passengersPercent = passengerCount / total
  const bothPercent = bothCount / total

  // Draw circles in segments (legend: drivers=success, passengers=primary, both=gray)
  drawCircle('#E4E6EF', options.lineWidth, 0, 1) // Base circle
  let cursor = 0
  if (bothPercent > 0) {
    drawCircle(
      getCSSVariableValue('--bs-secondary'),
      options.lineWidth,
      cursor,
      cursor + bothPercent
    )
    cursor += bothPercent
  }
  if (passengersPercent > 0) {
    drawCircle(
      getCSSVariableValue('--bs-primary'),
      options.lineWidth,
      cursor,
      cursor + passengersPercent
    )
    cursor += passengersPercent
  }
  if (driversPercent > 0) {
    drawCircle(
      getCSSVariableValue('--bs-success'),
      options.lineWidth,
      cursor,
      cursor + driversPercent
    )
  }
}
export {TotalUsers}
